
import {Request} from './Request';
import {Utils} from '../common/Utils';
import {BaseClass} from "../base/BaseClass";

/**
 * A model represents the response structure from the ARGO Rest API. It handles all CRUD (create, read, update, delete)
 * methods and parsing JSON from the backend to a readable object. An ajax request will be called with the
 * {@link Request} class.
 *
 * Important notice:
 * This model provides basic functions to work with the ARGO API. The parse function needs to be
 * overridden for every ARGO collection type, because the JSON structure differs completely.
 *
 */
export class BaseModel extends BaseClass {

  /**
   *
   * @param {Object} options - configuration options
   */
  constructor(options = {}) {

    super(options);

    /**
     * Properties (data) from the ARGO entry
     * @type {Object}
     */
    this.properties = {};
    /**
     * ARGO's collection type name
     * @type {String}
     * @private
     */
    this._collection = options.collection || 'dataEntries';
    /**
     * Reference to the collection class (parent) if exists
     * @type {Collection|null}
     * @private
     */
    this._listReference = options.listReference || null;
    /**
     * System properties from the data entry (wont be changed by PATCH requests)
     * @type {Object}
     * @private
     */
    this._sys = {};
    /**
     * Links to specific
     * @type {Object}
     * @private
     */
    this._links = {};
    /**
     * Unique system ID generated by ARGO
     * @type {Number}
     * @private
     */
    this._id = null;
    /**
     * Indicates properties from the model were modified.
     * @type {boolean}
     * @private
     */
    this._modified = false;
    /**
     * Changed properties until the last synchronisation. These properties
     * will be sent during the next {@link next} (PATCH request).
     * @type {Object}
     * @private
     */
    this._modifiedProps = {};
    /**
     * Previous values from modified properties.
     * @type {Object}
     * @private
     */
    this._prev = {};
    /**
     * Indicates the {@link destroy} function was called and will be removed
     * from the backend by next call of {@link save}.
     * @type {boolean}
     * @private
     */
    this._isDestroyed = false;
    /**
     * Indicates the model as read only, that means it cannot be updated.
     * Experimental and not fully implemented.
     * @type {boolean}
     * @private
     */
    this._readOnly = false;
    /**
     * Indicates the model is currently loading data from the backend.
     * @type {boolean}
     * @private
     */
    this._isLoading = false;

    // initialize model with already given data
    if (options.data) {
      this.parse(options.data, {silent: true});
    }
  }

  /**
   * ARGO's default system properties.
   * @return {Array<{name: String, dataType: String}>}
   */
  static getSysProperties() {
    return [{
      name: 'created', dataType: 'DATE'
    },{
      name: 'createdBy', dataType: 'STRING'
    },{
      name: 'lastModified', dataType: 'DATE'
    },{
      name: 'modifiedBy', dataType: 'STRING'
    }];
  }

  /**
   * Creates a new Request instance.
   * @param {RequestConfig} [options] - options passed to the Request class
   * @return {Request}
   * @private
   */
  _createRequest(options = {}) {
    options.collection = this._collection;
    return this._req = new Request(options)
      .before(() => {
        this._isLoading = true;
        this.emit('sync:loading', this);
      });
  }

  /**
   * Fetches an entry from a ARGO collection by using the query controller with
   * the given filter. Always the first entry will be used for the model.
   * @param {Array<QueryFilter>|QueryFilter} filters - query filter that identifies the entry
   * @param {RequestConfig} [options] - options passed to {@link Request} object
   * @return {Promise}
   */
  fetchByFilter(filters, options) {
    if (!Array.isArray(filters)) {
      filters = [ filters ];
    }

    let queryOptions = {
      filters,
    };
    this.triggerMethodOn('beforeFetchByFilter', [queryOptions]);

    return this._createRequest()
      .query(queryOptions)
      .then((data) => {
        this._isLoading = false;
        this.parseEmbedded(data);
      })
      .catch((err) => {
        this._isLoading = false;
        this.emit('sync:error', this, err);
        throw err;
      });
  }

  /**
   * Fetches an entry from a ARGO collection with given id.
   * @param {Number} id - unique system id that identifies an entry from a ARGO collection
   * @param {RequestConfig} [options] - options passed to {@link Request} object
   * @return {Promise}
   */
  fetchById(id, options = {}) {
    return this._createRequest(options)
      .get(id)
      .then((data) => {
        this._isLoading = false;
        this.parse(data);
      })
      .catch((err) => {
        this._isLoading = false;
        this.emit('sync:error', this, err);
        throw err;
      });
  }

  /**
   * Parse an ARGO response from a collection query. Always the first entry
   * will be taken and passed to {@link parse}.
   * @param {Object} data - response from the ARGO API
   */
  parseEmbedded(data) {
    if (data._embedded && data._embedded[this._collection] && data._embedded[this._collection].length) {
      this.parse(data._embedded[this._collection][0]);
    }
    else {
      // TODO: rethink if an error 404 should be thrown (for consistency with other model requests)
      this.parse({});
    }
  }

  /**
   * Parse a single response from the ARGO API.
   * @param {Object} data - response from the ARGO API
   * @param {Object} [options] -
   */
  parse(data, options = {}) {

    this.triggerMethodOn('beforeParse', [data, options]);

    // extract system properties
    this.parseSystemProperties(data);

    // extract unique system ID and links
    if (!this._id && data._links && data._links.self) {
      this._id = data._links.self.href.split('/').pop();
      this._readOnly = Utils.isUndefOrNull(this._id) || this._id <= 0;
    }
    this._links = data._links;
    delete data._links;

    // store data
    this.set(data, {silent: true});
    // reset tracking info
    this._modified = false;
    this._modifiedProps = {};
    this._prev = {};

    if (options.silent !== true) {
      this.emit('sync:done', this, !!options.isNew);
    }
  }

  /**
   * Extract system properties from the ARGO response defined in
   * {@link getSysProperties}
   * @param {Object} data - response from the ARGO API
   */
  parseSystemProperties(data) {
    this._sys = {};
    BaseModel.getSysProperties().forEach((prop) => {
      this._sys[prop.name] = data[prop.name];
      delete data[prop.name];
    });
  }

  /**
   * Returns the data object that will be used to sent to the backend.
   * @return {{properties: Object}}
   * @private
   */
  _getRequestData(props = this.properties) {
    return props;
  }

  /**
   * Save changes to the API. Send a POST request if the model is a new record otherwise
   * a PATCH request with properties that have changed will be sent.
   * @param options
   * @param {boolean} options.force - make always a patch request (also if the model hasn't changes)
   * @return {Promise|false} - returns the request or false, if no properties has been updated
   */
  save(options = {}) {

    // delete entry from remove server
    if (this._isDestroyed) {

      if (!this._id) {
        return false;
      }

      return this._createRequest()
        .delete(this._id)
        .then(() => {
          if (options.silent !== true) {
            this._isLoading = false;
            this.emit('delete:done', this);
          }
        })
        .catch((err) => {
          if (options.silent !== true) {
            this._isLoading = false;
            this.emit('delete:error', this, err);
          }
          throw err;
        });
    }

    //  new data entry -> send post request and extract new generated id
    if (!this._id) {
      return this._createRequest()
        .post(this._getRequestData())
        .then(() => {
          this._id = (this._req.response.xhr.getResponseHeader('Location')+'').split('/').pop();
          return new Request({collection: this._collection}).get(this._id); // re-fetch data to get AID value for pk
        })
        .then((data) => {
          this._isLoading = false;
          this.parse(data, {isNew: true, silent: options.silent});
        })
        .catch((err) => {
          if (options.silent !== true) {
            this._isLoading = false;
            this.emit('sync:error', this, err);
          }
          throw err;
        });
    }

    let isModified = this._modified;
    let changes = this._modifiedProps;

    // always make an update
    if (!isModified && options.force === true) {
      isModified = true;
      changes = this.properties;
    }

    if (!isModified) {
      return false;
    }

    // other update data -> patch request
    return this._createRequest()
      .patch(this._id, this._getRequestData(changes), this.callback)
      .then(() => {
        this._modified = false;
        this._modifiedProps = {};
        this._isLoading = false;
        if (options.silent !== true) {
          this.emit('sync:done', this);
        }
      })
      .catch((err) => {
        this._isLoading = false;
        if (options.silent !== true) {
          this.emit('sync:error', this, err);
        }
        throw err;
      });
  }

  /**
   * Returns true when the model was modified.
   * @return {boolean}
   */
  isModified() {
    return this._modified;
  }

  /**
   * Returns true if the model is empty.
   * @return {boolean}
   */
  isEmpty() {
    return $.isEmptyObject(this.properties);
  }

  /**
   * Returns true if model is doing a request otherwise false.
   * @return {boolean}
   */
  isLoading() {
    return this._isLoading;
  }

  /**
   * Checks the model has a property with given key and the value
   * is not undefined nor null.
   * @param {String} key - property name
   * @return {boolean}
   */
  has(key) {
    return !Utils.isUndefOrNull(this.properties[key]);
  }

  /**
   * Return the value from the property with the given name.
   * @param {String} key - property name
   * @return {*}
   */
  get(key) {
    return this.properties[key];
  }

  /**
   * Returns the unique ID from the entry behind the model
   * @return {null|number}
   */
  getId() {
    return this._id;
  }

  /**
   * Checks if the model data already exists on the server,
   * so that means the unique ID exists.
   * @return {boolean}
   */
  isSynchronized() {
    return this._id !== null;
  }

  /**
   * Returns all properties from the model.
   * @return {Object}
   */
  getData() {
    return this.properties;
  }

  /**
   * Returns the value from the system properties with given key or
   * all system properties if key is not set.
   * @param {String} [key] - name of the system property name
   * @return {*}
   */
  getSystemData(key) {
    if (key) {
      return this._sys.hasOwnProperty(key) ? this._sys[key] : null;
    }
    return this._sys;
  }

  /**
   * Set a property with given key and value or expect an key-value object as first argument.
   * @param {String|Object} key - name of the property or m object of key values
   * @param {*} value - value to be set
   * @param {*} [options] - settings
   * @return {BaseModel}
   */
  set(key, value, options = {}) {
    let props = {};

    if ($.isPlainObject(key)) {
      props = key;
      options = value || {};
    }
    else {
      props[key] = value;
    }

    let changed = {};
    let hasChanges = false;
    for (let propName in props) {
      let val = props[propName];

      // modified property TODO: arrays...
      if (!this.properties.hasOwnProperty(propName) || !this.equal(this.properties[propName], val)){
        this._prev[propName] = this.properties[propName];
        changed[propName] = this.properties[propName] = this._modifiedProps[propName] = val;
        hasChanges = true;
      }
    }

    // keep previous changed state
    this._modified = this._modified || hasChanges;

    if (options.silent !== true && hasChanges) {
      this.emit('change', this, changed);
    }

    return this;
  }

  /**
   * Clears the value for the property with the given name.
   * @param key
   * @return {BaseModel}
   */
  clear(key) {
    return this.set(key, null);
  }

  /**
   * Clears all properties and mark as destroyed. Use {@link save} to remove
   * the entry behind the model from the backend.
   * @param {Object} options
   * @return {BaseModel}
   */
  destroy(options = {}) {
    this._isDestroyed = true;
    this._modified = false;
    this._modifiedProps = {};
    this.properties = {};

    if (options.silent !== true) {
      this.emit('destroy', this);
    }
    return this;
  }

  /**
   * Reset the model with data before the last change.
   * @param {Object} [options] - options passed to the {@link set} function
   * @return {BaseModel}
   */
  revert(options = {}) {

    let prevKeys = Object.keys(this._prev);
    this.set(this._prev, options);
    this._prev = {};

    // check if some keys have changed before the last "set"
    prevKeys.forEach((it) => {
      delete this._modifiedProps[it];
    });
    this._modified = !$.isEmptyObject(this._modifiedProps);
    return this;
  }

  /**
   * Compares to values and returns true if equal.
   * @param {*} haystack - base value
   * @param {*} needle - value to compare with
   * @return {boolean}
   */
  equal(haystack, needle) {

    if (haystack === needle) {
      return true;
    }
    if (haystack === "" || haystack === undefined) haystack = null;
    if (needle === "" || needle === undefined) needle = null;

    // compare array indices
    if (Array.isArray(haystack) && Array.isArray(needle) && haystack.length === needle.length) {
      return haystack.every((value, index) => this.equal(value, needle[index]));
    }

    // boolean value vs. textual
    if (Utils.isBool(haystack) && (haystack ? "true" : "false") === needle ||
      Utils.isBool(needle) && (needle ? "true" : "false") === haystack) {
      return true;
    }

    // weak compare, allow "5" == 5
    return haystack == needle;
  }

}